import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import {
  GET_DRIVER_LIST,
  GET_ALL_PAYER,
  GET_SCHEPASSENGER_LIST,
  GET_ADD_TRIP_LIST,
} from "../constant";
import Swal from "../messageBox/customSweetAlert";
import { currentDateFormat } from "../utils";
import { useDispatch } from "react-redux";
import { setLoading } from "../redux/loaderAction";
import SearchComponent from "../components/SearchComponent";
import moment from "moment";
import TripTable from "../components/tables/TripTable";

const TripList = () => {
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tripList, setTripList] = useState([]);
  const [filteredTripList, setFilteredTripList] = useState([]);
  const [errors, setErrors] = useState({});
  const tableRef = useRef();

  const [passengerList, setPassengerList] = useState([]);
  const [driverList, setDriverList] = useState([]);
  const [payerList, setPayerList] = useState([]);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [payer, setPayer] = useState("0");
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [tripStatus, setTripStatus] = useState("3");
  const [showAllRowsForExport, setShowAllRowsForExport] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  useEffect(() => {
    fetchTrips();
    loadInitialData();
  }, []);

  const buildAddTripListParams = () => ({
    page: 1,
    limit: 10,
    startDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : "",
    endDate: toDate ? moment(toDate).format("YYYY-MM-DD") : "",
    passengerId: selectedPassenger?.value?.user_id || "",
    payerId: payer && payer !== "0" ? payer : "",
  });

  const parseAddTripListResponse = (response) => {
    const isOk =
      response?.data?.success === true ||
      response?.data?.status === 200 ||
      response?.data?.status === "200" ||
      response?.status === 200;
    if (!isOk) return [];
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const fetchTrips = async () => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(GET_ADD_TRIP_LIST, {
        params: buildAddTripListParams(),
      });
      const rows = parseAddTripListResponse(response);
      setTripList(rows);
      setFilteredTripList(rows);
    } catch (error) {
      console.error("Error fetching trips:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const loadInitialData = async () => {
    try {
      const [passengerResponse, driverResponse, payerResponse] =
        await Promise.all([
          axios.get(GET_SCHEPASSENGER_LIST),
          axios.get(GET_DRIVER_LIST),
          axios.get(GET_ALL_PAYER),
        ]);

      setPassengerList(passengerResponse.data);
      setDriverList(driverResponse.data);
      setPayerList(payerResponse.data);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const handlePayerChange = (e) => {
    setPayer(e.target.value);
  };

  const handleStatusChange = (e) => {
    setTripStatus(e.target.value);
  };

  const handleSearch = async () => {
    if (errors.from_date || errors.to_date) {
      Swal.fire(
        "Error",
        "Please correct the date errors before searching",
        "error"
      );
      return false;
    }
    setIsSearchLoading(true);
    dispatch(setLoading(true));
    try {
      const response = await axios.get(GET_ADD_TRIP_LIST, {
        params: buildAddTripListParams(),
      });
      const rows = parseAddTripListResponse(response);
      setTripList(rows);
      setFilteredTripList(rows);
    } catch (error) {
      console.error("Error fetching trips:", error);
    } finally {
      dispatch(setLoading(false));
      setIsSearchLoading(false);
    }
  };

  const driverListWithFullName = driverList.map((driver) => {
    const fullName = `${driver.first_name} ${driver.middle_name || ""} ${driver.last_name
      }`
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    return {
      ...driver,
      full_name: fullName,
    };
  });

  const passengerListWithFullName = passengerList.map((passenger) => {
    const fullName = `${passenger.first_name} ${passenger.middle_name || ""} ${passenger.last_name
      } ${passenger.passenger.pass_pmi_no || ""}`
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    return {
      ...passenger,
      full_name: fullName,
    };
  });

  const filterPayerName =
    payer === "0"
      ? "All"
      : payerList.find((p) => String(p.id) === String(payer))?.payer_name || "";
  const filterDriverName = selectedDriver?.label
    ? selectedDriver.label
    : selectedDriver?.value?.first_name
      ? selectedDriver.value?.first_name +
      " " +
      (selectedDriver.value?.middle_name || "") +
      (selectedDriver.value?.last_name || "")
      : "All";
  const filterPassengerName = selectedPassenger?.value?.first_name
    ? selectedPassenger.value.first_name +
    " " +
    (selectedPassenger.value.middle_name || "") +
    " " +
    (selectedPassenger.value.last_name || "")
    : "All";
  const filterTripStatusStr =
    tripStatus === "3"
      ? "All"
      : tripStatus === "1"
        ? "Completed"
        : tripStatus === "0"
          ? "Pending"
          : "";
  const filterFromDateStr = fromDate
    ? moment(fromDate).format("MM/DD/YYYY")
    : "";
  const filterToDateStr = toDate ? moment(toDate).format("MM/DD/YYYY") : "";

  return (
    <>
      <section style={{ borderBottom: "solid 1px #eee", marginTop: "0px" }}>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Add Trip</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>

      <section className="filter-section">
        <div
          className="d-flex justify-content-between filter-container"
          style={{ background: "rgb(205 228 218 / 29%)" }}
        >
          <div className="schedule-filter">
            <div className="me-2">
              <SearchComponent
                data={passengerListWithFullName}
                searchFields={["full_name"]}
                placeholder="Search Passenger"
                label="Passenger"
                onSelect={(selectedPassenger) => {
                  setSelectedPassenger(selectedPassenger);
                }}
              />
            </div>
            <div className="me-2">
              <SearchComponent
                data={driverListWithFullName}
                searchFields={["full_name"]}
                placeholder="Search Driver"
                label="Driver"
                onSelect={(selectedDriver) => {
                  setSelectedDriver(selectedDriver);
                }}
                onChange={(selectedDriver) => {
                  setSelectedDriver(selectedDriver);
                }}
              />
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>Payor</label>
              <select
                className="form-select"
                name="payer"
                value={payer}
                onChange={handlePayerChange}
              >
                <option value="0">All</option>
                {payerList.map((payer, index) => (
                  <option key={index} value={payer.id}>
                    {payer.payer_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="me-2">
              <label style={{ display: "block" }}>Status</label>
              <select
                className="form-select"
                name="status"
                value={tripStatus}
                onChange={handleStatusChange}
              >
                <option value="3">All</option>
                <option value="1">Completed</option>
                <option value="0">Pending</option>
              </select>
            </div>
            <div>
              <div className="me-2 date-container">
                <label style={{ display: "block" }}>From</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(date) => {
                    const today = new Date();
                    today.setHours(23, 59, 59, 999); // Set to end of today to allow today's date

                    if (date > today) {
                      setErrors({
                        from_date: "Cannot select future date",
                      });
                      return;
                    }
                    if (toDate && date > toDate) {
                      setErrors({
                        from_date: "From date greater than To date",
                      });
                    } else {
                      setErrors({});
                    }
                    setFromDate(date);
                  }}
                  maxDate={new Date()}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  className="datepicker form-control hasDatepicker"
                  id="from_date"
                  name="from_date"
                ></DatePicker>
              </div>
              <div className="error-message" style={{ maxWidth: "100%" }}>
                {errors.from_date && (
                  <span
                    className="errors"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "inline-block",
                    }}
                  >
                    {errors.from_date}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="me-2 date-container">
                <label style={{ display: "block" }}>To</label>
                <DatePicker
                  selected={toDate}
                  onChange={(date) => {
                    const today = new Date();
                    today.setHours(23, 59, 59, 999); // Set to end of today to allow today's date

                    if (date > today) {
                      setErrors({ to_date: "Cannot select future date" });
                      return;
                    }
                    if (fromDate && date < fromDate) {
                      setErrors({ to_date: "To date less than From date" });
                    } else {
                      setErrors({ from_date: "" });
                    }
                    setToDate(date);
                  }}
                  maxDate={new Date()}
                  dateFormat="MM/dd/yyyy"
                  autoComplete="off"
                  className="datepicker form-control hasDatepicker"
                  id="to_date"
                  name="to_date"
                ></DatePicker>
              </div>
              <div className="error-message" style={{ maxWidth: "100%" }}>
                {errors.to_date && (
                  <span
                    className="errors"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "inline-block",
                    }}
                  >
                    {errors.to_date}
                  </span>
                )}
              </div>
            </div>
            <div>
              <div>
                <button
                  className="btn btn-primary rounded-1"
                  type="button"
                  style={{
                    background: "#31876f",
                    border: "unset",
                    height: "38px",
                    color: "#fff",
                    padding: "7px",
                  }}
                  onClick={handleSearch}
                  disabled={isSearchLoading}
                >
                  {isSearchLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Searching...
                    </>
                  ) : (
                    "Search"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="table-responsive">
          <TripTable
            ref={tableRef}
            tableData={filteredTripList}
            showAllRowsForExport={showAllRowsForExport}
            filterPayer={filterPayerName}
            filterDriver={filterDriverName}
            filterPassenger={filterPassengerName}
            filterTripStatusStr={filterTripStatusStr}
            filterFromDate={filterFromDateStr}
            filterToDate={filterToDateStr}
            onRefreshTrips={fetchTrips}
          />
        </div>
      </section>
    </>
  );
};

export default TripList;
